import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@pentabd/ui";

import { CancelButton, Comments, ErrorModal, SuccessModal } from "@components";
import {
  BasicInfo,
  LeaveApprovalModal,
  LeaveDetailsHeader,
  LeaveDetailsModal,
  LeaveDetailsSummary,
  SupportingDocument,
} from "./components";

import {
  getDraftLeave,
  leaveApprovalAction,
  sendLeaveForApproval,
  updateLeave,
} from "@api/leave-apply";

import { LeaveApplyType } from "@type/HRM/leave-apply";

import {
  ApplyLeaveFormDataType,
  BASIC_INFO,
  MODAL,
  SEND_FOR_APPROVAL,
  applyLeaveFormValidation,
} from "@validations/HRM/leave/applyLeaveFormValidation";

import { yupResolver } from "@hookform/resolvers/yup";
import { useEmployeeServiceData } from "@hooks/useEmployeeServiceData";
import { useFunctionalityData } from "@hooks/useFunctionalityData";
import { useMasterData } from "@hooks/useMasterData";
import { useUser } from "@hooks/useUser";

import { restructureDocumentsData } from "@utils/documentDataConvert";
import { calculateDaysDifference } from "@utils/duration";
import { GetEmployeeDetails } from "@utils/getEmployeeDetails";

import { EMPLOYEE_SERVICE_DATA } from "@constants/employee-service-data";
import { FUNCTIONALITY_DATA } from "@constants/functionality-data";
import { MASTER_DATA } from "@constants/master-data";
import { ROUTES } from "@constants/routes";
import { STATUS } from "@constants/status.hrm";
import { TABS } from "@constants/tab-serial";
import { DECISION_TYPE } from "@constants/workflow-decision-type";

const { HRM } = ROUTES;
const { LEAVE, PROFILE } = HRM;
const { APPROVAL_MATRIX, LEAVE_TYPE } = FUNCTIONALITY_DATA;
const { SUCCESS, FAILED, DRAFT } = STATUS;
const { APPROVED, REJECTED } = DECISION_TYPE;

const LeaveDetails = () => {
  const [leaveData, setLeaveData] = useState<LeaveApplyType>({});
  const [modalTitle, setModalTitle] = useState<string>("");
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [showActionButton, setShowActionButton] = useState<boolean>(false);
  const [isStatusDraft, setIsStatusDraft] = useState<any>(true);
  const [isSendForApprovalModalOpen, setIsSendForApprovalModalOpen] =
    useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, id2 } = useParams();
  const methods = useForm<ApplyLeaveFormDataType>({
    resolver: yupResolver(applyLeaveFormValidation),
    values: {
      ...leaveData,
      ...restructureDocumentsData(leaveData?.leaveDocuments),
      delegatedEmployeeId: leaveData?.delegatedEmployee?.id,
    } as any,
  });

  const { user } = useUser();

  const {
    employeeMasterId,
    unitId,
    positionId: employeePositionId,
  } = user || {};
  const { watch, handleSubmit, setValue } = methods;

  let startDate = watch(BASIC_INFO.FROM_DATE);
  let endDate = watch(BASIC_INFO.TO_DATE);
  const positionId = watch(SEND_FOR_APPROVAL.DESIGNATION);
  const fileOnHand = watch(SEND_FOR_APPROVAL.FILE_ON_HAND);
  const description = watch(SEND_FOR_APPROVAL.COMMENT);
  const fileOnHandModal = watch(MODAL.FILE_ON_HAND);

  const { data: leaveType } = useFunctionalityData(LEAVE_TYPE);
  const { data: designation } = useFunctionalityData(APPROVAL_MATRIX);
  const { data: leaveNature } = useMasterData(MASTER_DATA.LEAVE_NATURE);
  const { data: commonInfoResponse } = useEmployeeServiceData({
    dataType: EMPLOYEE_SERVICE_DATA.COMMON_INFO,
    positionId: !!positionId ? positionId : "",
  });
  const commonInfo = commonInfoResponse?.commonInfo || [];
  const { employeeName, employeePosition, employeeImage } = GetEmployeeDetails(
    leaveData?.employee,
  );

  // leave modal
  const openModal = (status: string) => {
    setIsLeaveModalOpen(true);
    setModalTitle(status);
  };
  const closeModal = () => setIsLeaveModalOpen(false);
  const closeApprovalModal = () => setIsSendForApprovalModalOpen(false);

  // error and success modal
  const closeErrorModal = () => setIsErrorModalOpen(false);
  const successAction = () => {
    if (showActionButton) {
      navigate(LEAVE);
    } else navigate(`${PROFILE}?tab=${TABS.LEAVE}`);
  };

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  useEffect(() => {
    getDraftLeave(id as string, id2)
      .then((data) => setLeaveData(data))
      .catch((err) => console.log(err));
  }, [id, id2]);

  const onSubmit: SubmitHandler<ApplyLeaveFormDataType> = (
    data: LeaveApplyType,
  ) => {
    if (isStatusDraft) {
      updateLeave(data, (e) => {
        if (e.status === SUCCESS) {
          setIsSendForApprovalModalOpen(true);
        } else if (e.status === FAILED) setIsErrorModalOpen(true);
      });
    } else {
      leaveApprovalAction(
        {
          leaveDetailsId: leaveData?.id,
          employeeId: leaveData.employeeMasterId,
          description: data.comment,
          fileOnHand: fileOnHandModal,
          action: modalTitle,
        },
        (e) => {
          closeModal();
          if (e.status === SUCCESS) setIsSuccessModalOpen(true);
          else setIsErrorModalOpen(true);
        },
      );
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      setValue(
        "leaveDaysCount",
        `${calculateDaysDifference(
          startDate as string,
          endDate as string,
        )} Day${
          leaveData?.leaveDaysCount && leaveData?.leaveDaysCount > 0 ? "s" : ""
        }`,
      );
    }

    setShowActionButton(
      Number(employeeMasterId) === Number(leaveData.fileOnHand) &&
        leaveData?.status !== APPROVED &&
        leaveData?.status !== REJECTED,
    );

    setIsStatusDraft(
      leaveData?.status &&
        leaveData.status.toUpperCase() === DRAFT &&
        id &&
        Number(employeeMasterId) === Number(id),
    );
  }, [employeeMasterId, leaveData, id, startDate, endDate, setValue]);

  const handleSendForApproval = () => {
    sendLeaveForApproval(
      {
        leaveDetailsId: leaveData?.id,
        employeeId: leaveData?.employeeMasterId,
        description,
        fileOnHand,
      },
      (e) => {
        setIsSendForApprovalModalOpen(false);
        if (e.status === SUCCESS) {
          setIsSuccessModalOpen(true);
        } else if (e.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  return (
    <>
      {/* MODAL SECTION */}
      <>
        <SuccessModal isModalOpen={isSuccessModalOpen} action={successAction} />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>
      {/* MODAL SECTION */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <LeaveApprovalModal
            isModalOpen={isSendForApprovalModalOpen}
            onClose={closeApprovalModal}
            employeeName={employeeName}
            commonInfo={commonInfo}
            designation={designation}
            positionId={Number(positionId)}
            handleSendForApproval={handleSendForApproval}
          />
          <LeaveDetailsModal
            title={modalTitle}
            isModalOpen={isLeaveModalOpen}
            closeModal={closeModal}
            employeeName={employeeName}
            commonInfo={commonInfo}
            designation={designation}
            positionId={Number(positionId)}
          />
          <LeaveDetailsHeader
            data={{ employeeName, employeePosition, employeeImage }}
            openModal={openModal}
            showActionButton={showActionButton}
            employeePositionId={employeePositionId}
          />
          <div className="container p-10">
            <LeaveDetailsSummary employeeId={id as any} />
            {/* <ApprovalWorkFlow /> */}
            <BasicInfo
              unitId={unitId}
              leaveType={leaveType}
              leaveNature={leaveNature}
              isDisabled={!isStatusDraft}
            />
            {/* <RichTextEditor isDisabled={!isStatusDraft} /> */}
            <SupportingDocument isDisabled={!isStatusDraft} />
            {!isStatusDraft && (
              <Comments data={leaveData?.leaveApplicationHistory} />
            )}
            {isStatusDraft && (
              <div className="row pt-8">
                <div className="col-12 d-flex justify-content-end gap-6">
                  <CancelButton />
                  <Button
                    fill="fill"
                    size="sm"
                    type="primary"
                    htmlType="submit"
                  >
                    {t("LEAVE_APPLY.FORM.SUBMIT_BUTTON")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default LeaveDetails;
